'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import type { ButtonBlock as ButtonBlockType, PageSettings } from '@shared/types/salespads';
import type { EventData } from '@/lib/fetch-event-data';
import { pushDataLayer } from '@/lib/track-gtm';
import { resolveButtonSize, justifyMap } from '@/lib/block-sizes';

// Lazy-load BookingWidget only when needed for modal buttons
const BookingWidget = dynamic(
  () => import('../booking/BookingWidget').then(m => ({ default: m.BookingWidget })),
  { ssr: false, loading: () => (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  )}
);

interface Props {
  block: ButtonBlockType;
  settings: PageSettings;
  eventData?: EventData;
  salespadUrl?: string;
}

/**
 * Compute the button's class + style. Padding and font-size are NOT included
 * here — they come from the `.sp-btn` utility class via CSS variables set on
 * the element. This keeps both viewport sizes in a single element.
 */
function getButtonStyles(data: ButtonBlockType['data'], settings: PageSettings) {
  const buttonColor = data.color || settings.colorPrimary;
  let className = 'sp-btn inline-block rounded-lg font-semibold transition-all';
  const style: React.CSSProperties = {};
  const isGradient = buttonColor.includes('gradient');

  if (data.style === 'primary') {
    className += ' text-white hover:opacity-90';
    style.background = buttonColor;
  } else if (data.style === 'secondary') {
    className += ' bg-gray-100 text-gray-900 hover:bg-gray-200';
  } else if (data.style === 'outline') {
    className += ' border-2 bg-transparent hover:opacity-80';
    if (isGradient) {
      style.borderImage = `${buttonColor} 1`;
      style.background = buttonColor;
      style.backgroundClip = 'text';
      style.WebkitBackgroundClip = 'text';
      style.color = 'transparent';
    } else {
      style.borderColor = buttonColor;
      style.color = buttonColor;
    }
  }

  if (data.textColor) {
    style.color = data.textColor;
  }

  if (data.fullWidth) {
    className += ' w-full text-center';
  }

  return { className, style };
}

/** CSS variables for the .sp-btn class (padding + font-size per viewport). */
function getButtonSizeVars(data: ButtonBlockType['data']): React.CSSProperties {
  const size = resolveButtonSize(data.size, data.sizeMobile);
  return {
    '--sp-btn-pad-m': size.mobilePad,
    '--sp-btn-pad-d': size.desktopPad,
    '--sp-btn-fs-m': size.mobileFs,
    '--sp-btn-fs-d': size.desktopFs,
  } as React.CSSProperties;
}

/** CSS variables for the wrapping .sp-btn-wrap class (alignment per viewport). */
function getWrapperVars(data: ButtonBlockType['data']): React.CSSProperties {
  const alignDesktop = justifyMap[data.align] || 'center';
  const alignMobile = justifyMap[data.alignMobile ?? data.align] || alignDesktop;
  return {
    '--sp-align-m': alignMobile,
    '--sp-align-d': alignDesktop,
  } as React.CSSProperties;
}

function LinkButton({ data, settings, href, blockId }: { data: ButtonBlockType['data']; settings: PageSettings; href: string; blockId: string }) {
  const { className, style } = getButtonStyles(data, settings);
  const clickAction = data.clickAction || 'link';
  return (
    <a
      href={href}
      className={className}
      style={{ ...style, ...getButtonSizeVars(data) }}
      onClick={() => {
        pushDataLayer('salescal.salespad_click', {
          blockId,
          blockType: 'button',
          label: data.text,
          url: href,
          clickAction,
        });
      }}
    >
      {data.text}
    </a>
  );
}

function ModalButton({ data, settings, eventData, blockId }: { data: ButtonBlockType['data']; settings: PageSettings; eventData?: EventData; blockId: string }) {
  const [open, setOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState<string>('form');
  const { className, style } = getButtonStyles(data, settings);

  // Preload booking widget JS after page settles so modal opens instantly
  useEffect(() => {
    const timer = setTimeout(() => {
      import('../booking/BookingWidget').catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Mirror the widget's own width rule (applies to BOTH the standard and slide
  // form widgets — they share the exact rule): narrow (max-w-md) ONLY on a
  // calendar-less form step; every other state (calendar / confirmation /
  // disqualified, or a form with the calendar beside it) is the wide
  // side-by-side layout. `bookingStep` is reported by both widgets via
  // onStepChange, so this stays in sync — including slide forms, which DO need
  // the wide modal when they show a calendar (omitting them squashed the form).
  const showCalendarOnForm = eventData?.event.show_calendar_on_form ?? true;
  const needsWideModal = !!eventData && (
    bookingStep !== 'form' || showCalendarOnForm
  );

  return (
    <>
      <button
        onClick={() => {
          pushDataLayer('salescal.salespad_click', {
            blockId,
            blockType: 'button',
            label: data.text,
            url: '',
            clickAction: 'modal',
          });
          // Reset to the form step on (re)open so the modal opens at the
          // correct width — the widget always remounts on the form step.
          setBookingStep('form');
          setOpen(true);
        }}
        className={`${className} cursor-pointer`}
        style={{ ...style, ...getButtonSizeVars(data) }}
      >
        {data.text}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative transition-all duration-300 ${needsWideModal ? 'max-w-4xl' : 'max-w-lg'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg width={20} height={20} className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
            {eventData ? (
              <BookingWidget
                event={eventData.event}
                fields={eventData.fields}
                ruleSets={eventData.ruleSets}
                profile={eventData.profile}
                userEmail={eventData.profile.email}
                onStepChange={setBookingStep}
                metaPixelIds={eventData.metaPixelIds}
                teamId={eventData.teamId}
                metaPixelEventNames={eventData.metaPixelEventNames}
                confirmationIncludeCancelReschedule={eventData.confirmationIncludeCancelReschedule}
                gtmContainerId={eventData.gtmContainerId}
                eventSlug={eventData.event.slug}
                bookingPaused={eventData.bookingPaused}
              />
            ) : (
              <div className="text-center py-12 text-gray-400">
                {data.eventId ? 'Event not found or not published' : 'No event selected'}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function ButtonBlock({ block, settings, eventData, salespadUrl }: Props) {
  const { data } = block;
  const clickAction = data.clickAction || 'link';
  const wrapperStyle = getWrapperVars(data);

  if (clickAction === 'modal') {
    return (
      <div className="sp-btn-wrap" style={wrapperStyle}>
        <ModalButton data={data} settings={settings} eventData={eventData} blockId={block.id} />
      </div>
    );
  }

  const href = clickAction === 'salespad' && salespadUrl
    ? salespadUrl
    : data.url || '#';

  return (
    <div className="sp-btn-wrap" style={wrapperStyle}>
      <LinkButton data={data} settings={settings} href={href} blockId={block.id} />
    </div>
  );
}
